// 5.50.0 calendar notices — pure helpers (tested in calendarNotices.test.js) for the ONE "Today" card (Main + HealthSherpa
// pages) and the red Today panel (Calendar page): which reminders are firing and which Today row they belong to, what the
// card shows, and where the card sits (per computer, in localStorage, clamped on-screen).

// A reminder dismissed in one window must vanish in every other open window at once (the server already keeps it
// dismissed for this user everywhere; this only saves the other windows from waiting for their next poll).
export const REMINDER_DISMISSED_EVENT = 'insuredocs-calendar-reminder-dismissed';
export const REMINDER_DISMISSED_KEY = 'insuredocs.calendar.reminderDismissed';

export function announceReminderDismissed(key) {
  try { window.dispatchEvent(new CustomEvent(REMINDER_DISMISSED_EVENT, { detail: key })); } catch (_e) { /* ignore */ }
  try { localStorage.setItem(REMINDER_DISMISSED_KEY, JSON.stringify({ key, at: Date.now() })); } catch (_e) { /* ignore */ }
}

// The reminder key carried by a storage event (or null).
export function dismissedKeyFromStorage(e) {
  if (!e || e.key !== REMINDER_DISMISSED_KEY || !e.newValue) return null;
  try { return JSON.parse(e.newValue).key || null; } catch (_e) { return null; }
}

// Pair every firing reminder with its Today row (same object + kind; for a repeating event the occurrence with the same
// start when there is one). Returns { alerts: [{ reminder, item | null }], rest: Today rows not alerting }.
// A reminder for something not in today's list (e.g. a reminder the day before, or another own calendar) still alerts.
export function matchReminders(items, reminders) {
  const rest = [...(items || [])];
  const alerts = [];
  for (const r of reminders || []) {
    const cands = rest.filter((i) => i.object_id === r.object_id && i.kind === r.kind);
    const item = cands.find((i) => r.start && i.start === r.start) || cands[0] || null;
    if (item) rest.splice(rest.indexOf(item), 1);
    alerts.push({ reminder: r, item });
  }
  return { alerts, rest };
}

// What to draw. today = GET /api/calendar/today (or null); alerts = matchReminders(...).alerts.
//   'card' — open: the alerts on top, then today's rows when the Today list is open; showReopen when it is minimized
//   'tab'  — minimized and nothing alerting: the small tab
//   'none' — nothing for today (or closed) and nothing alerting
// A firing reminder always shows (like the old pop-up) even when the Today list was minimized or closed; only its own
// Dismiss clears it.
export function noticeView(today, alerts) {
  const hasToday = !!(today && today.enabled && today.count > 0);
  const state = today ? today.state : 'open';
  const n = (alerts || []).length;
  if (n) return { mode: 'card', showToday: hasToday && state === 'open', showReopen: hasToday && state === 'minimized', canHide: hasToday && state === 'open' };
  if (hasToday && state === 'open') return { mode: 'card', showToday: true, showReopen: false, canHide: true };
  if (hasToday && state === 'minimized') return { mode: 'tab', showToday: false, showReopen: false, canHide: false };
  return { mode: 'none', showToday: false, showReopen: false, canHide: false };
}

// ---- position: distance from the RIGHT and BOTTOM edges (so the default stays bottom-right on any screen) -----------
export const CARD_POS_KEY = 'insuredocs.calendar.cardPos.v1';
export const DEFAULT_POS = { right: 16, bottom: 40 };

// Keep the whole card on screen (w / h = the card's size, vw / vh = the window's).
export function clampPos(pos, w, h, vw, vh) {
  const p = pos || DEFAULT_POS;
  const maxRight = Math.max(0, vw - w);
  const maxBottom = Math.max(0, vh - h);
  const num = (v, d) => (Number.isFinite(v) ? v : d);
  return {
    right: Math.round(Math.min(Math.max(num(p.right, DEFAULT_POS.right), 0), maxRight)),
    bottom: Math.round(Math.min(Math.max(num(p.bottom, DEFAULT_POS.bottom), 0), maxBottom)),
  };
}

export function loadPos(storage = window.localStorage) {
  try {
    const v = JSON.parse(storage.getItem(CARD_POS_KEY) || 'null');
    if (v && Number.isFinite(v.right) && Number.isFinite(v.bottom)) return { right: v.right, bottom: v.bottom };
  } catch (_e) { /* ignore */ }
  return null;
}

export function savePos(pos, storage = window.localStorage) {
  try { storage.setItem(CARD_POS_KEY, JSON.stringify({ right: Math.round(pos.right), bottom: Math.round(pos.bottom) })); } catch (_e) { /* ignore */ }
}

export function clearPos(storage = window.localStorage) {
  try { storage.removeItem(CARD_POS_KEY); } catch (_e) { /* ignore */ }
}

// The new position while dragging: the pointer moved (dx, dy) from where the drag started.
export const dragPos = (start, dx, dy) => ({ right: start.right - dx, bottom: start.bottom - dy });
